import { replace } from 'connected-react-router';
import {
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_AVATAR,
    PB_POST_HAS_BEEN_SUBMITTED,
    USER_ALREADY_LIKED,
    USER_ALREADY_UNLIKED,
    USER_LIKED,
    USER_UNLIKED,
} from 'mk/autogenerated/translations/ProfileDetail.sagas.01ceb0f9d3d01565320ddcd9d260ce85'
import {
    blogs_api_create_blog_post_url,
    blogs_api_profile_delete_avatar_url,
    blogs_api_profile_detail_url,
    blogs_api_profile_feed_get_url,
    blogs_api_profile_fetch_related_users_url,
    blogs_api_profile_fetch_users_groups_url,
    blogs_api_profile_set_like_url,
} from 'mk/urls/functions';
import { BLOG_POST_CREATE_FORM_NAME } from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail';
import {
    blogPostCreateApi,
    profileDeleteAvatarByAdminApi,
    profileDetailFetchApi,
    profileExpandAvatarGridApi,
    profileExpandGroupListApi,
    profileFeedFetchApi,
    profileFeedScrollToTrigger,
    profileSetLikeUserApi,
    BlogPostCreateNormalizedResponse,
    BlogPostCreateTriggerAction,
    BLOG_POST_CREATE_TRIGGER,
    ProfileDeleteAvatarByAdminNormalizedResponse,
    ProfileDeleteAvatarByAdminTriggerAction,
    ProfileDetailFetchNormalizedResponse,
    ProfileDetailFetchTriggerAction,
    ProfileExpandAvatarGridNormalizedResponse,
    ProfileExpandAvatarGridTriggerAction,
    ProfileExpandGroupListNormalizedResponse,
    ProfileExpandGroupListTriggerAction,
    ProfileFeedFetchNormalizedResponse,
    ProfileFeedFetchTriggerAction,
    ProfileFeedScrollToTriggerAction,
    ProfileSetLikeUserTriggerAction,
    PROFILE_DELETE_AVATAR_BY_ADMIN_TRIGGER,
    PROFILE_DETAIL_FETCH_TRIGGER,
    PROFILE_EXPAND_AVATAR_GRID_TRIGGER,
    PROFILE_EXPAND_GROUP_LIST_TRIGGER,
    PROFILE_FEED_FETCH_TRIGGER,
    PROFILE_FEED_SCROLLTO_TRIGGER,
    PROFILE_SET_LIKE_USER_TRIGGER,
} from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.actions';
import { ProfileDetailData } from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.reducers';
import { ScrollToPosition } from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetailPage';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import { KidSchema } from 'mk2/apps/settings/schemas';
import { ComplaintEntity, ComplaintSchema } from 'mk2/containers/Complaint/Complaint.schemas';
import { toolbarCloseAll } from 'mk2/containers/Toolbar/Toolbar.actions';
import {
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
    XHRAction,
    XHRFormError,
    XHRGetError,
    XHRPostError,
} from 'mk2/helpers/api';
import { mergeDenormalized, traverseAndMergeDenormalized } from 'mk2/helpers/denormalized';
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from 'mk2/helpers/form';
import { PhotoUploadStatus } from 'mk2/helpers/form.reducers';
import { scrollElementTo } from 'mk2/helpers/scrolling';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { tupdate } from 'mk2/helpers/types';
import { removeEmpty, removePage1, url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import { getRequestDeviceMobile } from 'mk2/reducers';
import {
    BlogPostEntity,
    BlogPostSchema,
    GroupSchema,
    PhotoEntity,
    PhotoSchema,
    ProVendorSchema,
    UserEntity,
    UserSchema,
} from 'mk2/schemas';
import { normalize } from 'normalizr';
import { parse } from 'query-string';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { isNumber } from 'util';

const logger = getLogger('blogs/ProfileDetail.sagas');

interface ApiResponseFetchProfileDetail {
    body: ProfileDetailData & {
        entities: ProfileDetailFetchNormalizedResponse['entities'];
        result: { userStrollers: number[]; userStrollersReviews: [] };
    };
}

function* fetchProfileDetail({ username, xhr }: ProfileDetailFetchTriggerAction & XHRAction) {
    yield put(profileDetailFetchApi.request(username));

    try {
        const response: ApiResponseFetchProfileDetail = yield call(
            () => xhr.get(blogs_api_profile_detail_url(), { username }),
        );

        const normalizedResponse: ProfileDetailFetchNormalizedResponse = normalize(
            response.body,
            {
                ambassadorBrandLogo: PhotoSchema,
                followers: {users: [UserSchema]},
                following: {users: [UserSchema]},
                kids: [KidSchema],
                memberOfGroups: {groups: [GroupSchema]},
                user: UserSchema,
                userAvatarPhoto: PhotoSchema,
                userVendorData: ProVendorSchema,
            },
        );

        // TODO: Remove when all entities will be normalized on server
        const mergedResponse = mergeDenormalized(normalizedResponse, {
            entities: response.body.entities,
            result: response.body.result,
        });

        yield put(profileDetailFetchApi.success(username, mergedResponse));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(profileDetailFetchApi.failure(username, normalizeError(error)));
    }
}

interface ApiResponseFetchProfileFeed {
    body: {
        entities: ProfileFeedFetchNormalizedResponse['entities'];
        result: {
            complaints: ComplaintEntity[];
        };
        hasComplaints: boolean;
        page: number;
        posts: BlogPostEntity[];
    };
}

function* fetchProfileFeed({ username, currentUrl, numPostsBeforeLoad, pageRef, postType, query, xhr }: ProfileFeedFetchTriggerAction & XHRAction) {
    const qs = parse(currentUrl.search);

    if (postType === null) {
        const gotoUrl = url(blogsProfileUrl, {username});
        yield put(replace(gotoUrl));
        return;
    }

    if (numPostsBeforeLoad > 0 && qs.scrollto) {
        // if data is already loaded, scroll to bottom immediately
        // so that user does not experience scroll delayed by api call
        yield put(profileFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
    }

    yield put(profileFeedFetchApi.request(username, pageRef, postType, query));

    try {
        const response: ApiResponseFetchProfileFeed = yield call(
            () => xhr.get(blogs_api_profile_feed_get_url(), removeEmpty({
                username,
                page: pageRef,
                post_type: postType,
                query,
            })),
        );

        // ak skusame nahravat zly page number (negativny, vacsi ako pocet stranok, atd.), backend nas redirectne
        if (isNumber(pageRef) && pageRef !== response.body.page) {
            throw new Error('Wrong page');
        }

        const normalizedResponse: ProfileFeedFetchNormalizedResponse = normalize(response.body, {
            complaints: [ComplaintSchema],
            posts: [BlogPostSchema],
        });

        // TODO: Remove when all entities will be normalized on server
        let mergedResponse = mergeDenormalized(normalizedResponse, {
            entities: response.body.entities,
            result: response.body.result,
        });

        // TODO: Remove when all entities will be normalized on server
        mergedResponse = traverseAndMergeDenormalized(mergedResponse, mergedResponse);

        yield put(profileFeedFetchApi.success(
            username,
            pageRef,
            postType,
            query,
            response.body.page,
            response.body.hasComplaints,
            mergedResponse,
        ));

        if (qs.page === '1') {
            // remove ?page=1 from url
            const gotoUrl = url(blogsProfileUrl, {username}, removeEmpty(removePage1({ ...qs, page: 1 })));
            yield put(replace(gotoUrl));
        }

        const numPostsAfterLoad = response.body.posts ? response.body.posts.length : 0;
        if (numPostsBeforeLoad !== numPostsAfterLoad && qs.scrollto) {
            // other count of posts, either first load, or data on server changed, so scroll again
            yield put(profileFeedScrollToTrigger(qs.scrollto as ScrollToPosition));
        }

    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(profileFeedFetchApi.failure(username, pageRef, postType, query, normalizeError(error)));
    }
}

function* scrolltoProfileFeed({ scrollto }: ProfileFeedScrollToTriggerAction) {
    const isMobile = yield select(getRequestDeviceMobile);
    if (scrollto === ScrollToPosition.LastPost) {
        scrollElementTo('#scrolltoLastHere', 'window-bottom', 0);
    } else if (scrollto === ScrollToPosition.FirstPost) {
        scrollElementTo('#scrolltoFirstHere', 'window-top', isMobile ? 200 : 45);
    }
}

interface ApiResponseSetLikeUser {
    body: {
        like: boolean;
        changed: boolean;
    };
}

function* setLikeUser({ username, like, xhr }: ProfileSetLikeUserTriggerAction & XHRAction) {
    yield put(profileSetLikeUserApi.request(username, like));

    try {
        const response: ApiResponseSetLikeUser = yield call(
            () => xhr.post(blogs_api_profile_set_like_url(), { username, like }),
        );

        yield put(profileSetLikeUserApi.success(username, response.body.like, {}));

        if (like !== response.body.like) {
            throw new Error('something went wrong');
        }

        if (response.body.changed) {
            yield showSuccessToast(response.body.like ? USER_LIKED : USER_UNLIKED);
        } else {
            yield showSuccessToast(response.body.like ? USER_ALREADY_LIKED : USER_ALREADY_UNLIKED);
        }

    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(profileSetLikeUserApi.failure(username, like, normalizeError(error)));
    }
}

interface ApiResponseDeleteAvatarByAdmin {
    body: {
        user: UserEntity;
        userAvatarPhoto: PhotoEntity;
    };
}

function* deleteAvatar({ username, xhr }: ProfileDeleteAvatarByAdminTriggerAction & XHRAction) {
    if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_AVATAR)) {
        return;
    }

    yield put(profileDeleteAvatarByAdminApi.request(username));

    try {
        const response: ApiResponseDeleteAvatarByAdmin = yield call(
            () => xhr.post(blogs_api_profile_delete_avatar_url(), { username }),
        );
        const normalizedResponse: ProfileDeleteAvatarByAdminNormalizedResponse = normalize(response.body, {
            user: UserSchema,
            userAvatarPhoto: PhotoSchema,
            avatarPost: BlogPostSchema,
        });
        yield put(profileDeleteAvatarByAdminApi.success(username, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(profileDeleteAvatarByAdminApi.failure(username, normalizeError(error)));
    }
}

function* expandAvatarGrid({ avatarGridType, username, xhr }: ProfileExpandAvatarGridTriggerAction & XHRAction) {
    yield put(profileExpandAvatarGridApi.request(avatarGridType, username));

    try {
        const response: ApiResponseDeleteAvatarByAdmin = yield call(
            () => xhr.get(blogs_api_profile_fetch_related_users_url(), { avatarGridType, username }),
        );

        const normalizedResponse: ProfileExpandAvatarGridNormalizedResponse = normalize(response.body, {
            users: [UserSchema],
        });

        yield put(profileExpandAvatarGridApi.success(avatarGridType, username, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(profileExpandAvatarGridApi.failure(avatarGridType, username, normalizeError(error)));
    }
}

function* expandGroupList({ username, xhr }: ProfileExpandGroupListTriggerAction & XHRAction) {
    yield put(profileExpandGroupListApi.request(username));

    try {
        const response: ApiResponseDeleteAvatarByAdmin = yield call(
            () => xhr.get(blogs_api_profile_fetch_users_groups_url(), { username }),
        );

        const normalizedResponse: ProfileExpandGroupListNormalizedResponse = normalize(response.body, {
            groups: [GroupSchema],
        });

        yield put(profileExpandGroupListApi.success(username, normalizedResponse));
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(profileExpandGroupListApi.failure(username, normalizeError(error)));
    }
}

export interface ApiResponsePostCreate {
    body: {
        post: BlogPostEntity;
    };
}

function* postCreate({ username, postFd, xhr }: BlogPostCreateTriggerAction & XHRAction) {
    yield put(blogPostCreateApi.request(username, postFd));

    const formName = BLOG_POST_CREATE_FORM_NAME;

    try {
        const response: ApiResponsePostCreate = yield call(
            () => xhr.post(blogs_api_create_blog_post_url(), {
                postData: tupdate(postFd, {
                    photos: postFd.photos ? postFd.photos.filter((photo) => photo.status === PhotoUploadStatus.SUCCESS) : [],
                }),
            }),
        );

        const normalizedResponse: BlogPostCreateNormalizedResponse = normalize(response.body, {
            post: BlogPostSchema,
        });

        // close toolbar, e.g. photos might be opened
        yield put(toolbarCloseAll(formName));

        yield handleXHRFormSuccessSaga(formName, null, PB_POST_HAS_BEEN_SUBMITTED, {
            perex: '',
            photos: [],
            sticker: null,
            publicVisibility: postFd.publicVisibility,
        });
        yield put(blogPostCreateApi.success(username, postFd, normalizedResponse));
    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(blogPostCreateApi.failure(username, postFd, normalizeError(error)));
    }
}
export default function* root() {
    yield all([
        takeLatest(PROFILE_DETAIL_FETCH_TRIGGER, fetchProfileDetail),
        takeLatest(PROFILE_EXPAND_AVATAR_GRID_TRIGGER, expandAvatarGrid),
        takeLatest(PROFILE_EXPAND_GROUP_LIST_TRIGGER, expandGroupList),
        takeLatest(PROFILE_FEED_FETCH_TRIGGER, fetchProfileFeed),
        takeLatest(PROFILE_FEED_SCROLLTO_TRIGGER, scrolltoProfileFeed),
        takeLatest(PROFILE_SET_LIKE_USER_TRIGGER, setLikeUser),
        takeLatest(PROFILE_DELETE_AVATAR_BY_ADMIN_TRIGGER, deleteAvatar),
        takeLatest(BLOG_POST_CREATE_TRIGGER, postCreate),
    ]);
}
